import { supabase } from "@/integrations/supabase/client";
import { todayInWorkspaceTz } from "@/hooks/useWorkspaceTasks";
import { isTaskOverduePending } from "@/lib/task-urgency";

export type TaskMetricsPeriod = "today" | "7d" | "30d";

export interface TaskMetricRow {
  member_id: string;
  display_name: string | null;
  total_assigned: number;
  completed: number;
  pending: number;
  overdue: number;
  positive_count: number;
  negative_count: number;
  completion_rate: number;
}

export interface TaskForMetrics {
  assigned_member_id: string;
  status: "pending" | "completed" | "cancelled";
  outcome: "positive" | "negative" | null;
  due_date: string | null;
  due_at?: string | null;
  completed_at: string | null;
  updated_at: string;
}

export interface MemberForMetrics {
  id: string;
  display_name: string | null;
}

function addDaysISO(dateIso: string, days: number): string {
  const d = new Date(dateIso + "T12:00:00Z");
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function maxDateISO(a: string, b: string): string {
  return a >= b ? a : b;
}

function isBetween(date: string, from: string, to: string): boolean {
  return date >= from && date <= to;
}

/** Data local (YYYY-MM-DD) de um instante ISO no fuso do workspace. */
export function toLocalDateISO(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(iso));
}

export function periodToMetricDates(
  period: TaskMetricsPeriod,
  timezone = "America/Sao_Paulo",
): { from: string; to: string } {
  const today = todayInWorkspaceTz(timezone);
  if (period === "today") return { from: today, to: today };
  if (period === "7d") return { from: addDaysISO(today, -6), to: today };
  return { from: addDaysISO(today, -29), to: today };
}

/**
 * Agrega métricas por membro (mesma regra da RPC corrigida).
 * Conclusões pelo completed_at no período; tarefas futuras concluídas antecipadamente entram.
 */
export function computeWorkspaceTaskMetrics(
  members: MemberForMetrics[],
  tasks: TaskForMetrics[],
  from: string,
  to: string,
  timezone = "America/Sao_Paulo",
): TaskMetricRow[] {
  const today = todayInWorkspaceTz(timezone);
  const dueHorizonTo = maxDateISO(to, addDaysISO(today, 90));

  const rows = members.map((member) => {
    const memberTasks = tasks.filter((t) => t.assigned_member_id === member.id);

    let total_assigned = 0;
    let completed = 0;
    let pending = 0;
    let overdue = 0;
    let positive_count = 0;
    let negative_count = 0;

    for (const t of memberTasks) {
      if (t.status === "cancelled") continue;

      const completedLocal =
        t.status === "completed"
          ? toLocalDateISO(t.completed_at ?? t.updated_at, timezone)
          : null;

      const completedInPeriod =
        completedLocal !== null && isBetween(completedLocal, from, to);

      const inDueHorizon = t.due_date ? isBetween(t.due_date, from, dueHorizonTo) : false;
      const countsAsAssigned = inDueHorizon || completedInPeriod;

      if (countsAsAssigned) total_assigned += 1;
      if (completedInPeriod) {
        completed += 1;
        if (t.outcome === "positive") positive_count += 1;
        if (t.outcome === "negative") negative_count += 1;
      }
      if (
        t.status === "pending" &&
        t.due_date &&
        isBetween(t.due_date, today, dueHorizonTo) &&
        !isTaskOverduePending(t, timezone)
      ) {
        pending += 1;
      }
      if (isTaskOverduePending(t, timezone)) {
        overdue += 1;
      }
    }

    const completion_rate =
      total_assigned > 0
        ? Math.round((completed / total_assigned) * 1000) / 10
        : 0;

    return {
      member_id: member.id,
      display_name: member.display_name,
      total_assigned,
      completed,
      pending,
      overdue,
      positive_count,
      negative_count,
      completion_rate,
    };
  });

  return rows.sort((a, b) =>
    (a.display_name ?? "").localeCompare(b.display_name ?? "", "pt-BR"),
  );
}

export function memberHasMetricActivity(row: TaskMetricRow): boolean {
  return (
    row.total_assigned > 0 ||
    row.completed > 0 ||
    row.pending > 0 ||
    row.overdue > 0 ||
    row.positive_count > 0 ||
    row.negative_count > 0
  );
}

export interface TaskPerformanceMetricRow extends TaskMetricRow {
  pending_root: number;
  pending_subtasks: number;
  avg_completion_days: number | null;
  late_completion_rate: number;
  overdue_rate_snapshot: number;
  delay_risk_index: number;
  subtask_completion_rate: number | null;
}

export interface TaskPerformanceSummary {
  total_assigned: number;
  completed: number;
  pending: number;
  pending_subtasks: number;
  overdue: number;
  positive_count: number;
  negative_count: number;
  completion_rate: number;
  avg_completion_days: number | null;
  overdue_rate_snapshot: number;
  avg_delay_risk_index: number | null;
}

export interface TaskPerformanceTimeseriesPoint {
  bucket_date: string;
  member_id: string;
  display_name: string | null;
  completed: number;
  completed_late: number;
  positive_count: number;
  negative_count: number;
}

function rpcProjectIdArg(
  projectId?: string | null,
): { p_project_id: string } | Record<string, never> {
  return projectId ? { p_project_id: projectId } : {};
}

function mapPerformanceMetricRow(
  row: Record<string, unknown>,
): TaskPerformanceMetricRow {
  return {
    member_id: String(row.member_id),
    display_name: (row.display_name as string | null) ?? null,
    total_assigned: Number(row.total_assigned ?? 0),
    completed: Number(row.completed ?? 0),
    pending: Number(row.pending ?? 0),
    overdue: Number(row.overdue ?? 0),
    positive_count: Number(row.positive_count ?? 0),
    negative_count: Number(row.negative_count ?? 0),
    completion_rate: Number(row.completion_rate ?? 0),
    pending_root: Number(row.pending_root ?? 0),
    pending_subtasks: Number(row.pending_subtasks ?? 0),
    avg_completion_days:
      row.avg_completion_days != null ? Number(row.avg_completion_days) : null,
    late_completion_rate: Number(row.late_completion_rate ?? 0),
    overdue_rate_snapshot: Number(row.overdue_rate_snapshot ?? 0),
    delay_risk_index: Number(row.delay_risk_index ?? 0),
    subtask_completion_rate:
      row.subtask_completion_rate != null
        ? Number(row.subtask_completion_rate)
        : null,
  };
}

/** Métricas estendidas via RPC (gestor/admin; inclui subtarefas opcionalmente). */
export async function fetchWorkspaceTaskPerformanceMetrics(
  workspaceId: string,
  from: string,
  to: string,
  includeSubtasks = true,
  projectId?: string | null,
): Promise<TaskPerformanceMetricRow[]> {
  const { data, error } = await supabase.rpc(
    "get_workspace_task_performance_metrics",
    {
      p_workspace_id: workspaceId,
      p_from: from,
      p_to: to,
      p_include_subtasks: includeSubtasks,
      ...rpcProjectIdArg(projectId),
    },
  );

  if (error) throw error;
  return (data ?? []).map((row) =>
    mapPerformanceMetricRow(row as Record<string, unknown>),
  );
}

export async function fetchWorkspaceTaskPerformanceSummary(
  workspaceId: string,
  from: string,
  to: string,
  projectId?: string | null,
): Promise<TaskPerformanceSummary> {
  const { data, error } = await supabase.rpc(
    "get_workspace_task_performance_summary",
    {
      p_workspace_id: workspaceId,
      p_from: from,
      p_to: to,
      ...rpcProjectIdArg(projectId),
    },
  );

  if (error) throw error;

  const row = (data?.[0] ?? {}) as Record<string, unknown>;
  return {
    total_assigned: Number(row.total_assigned ?? 0),
    completed: Number(row.completed ?? 0),
    pending: Number(row.pending ?? 0),
    pending_subtasks: Number(row.pending_subtasks ?? 0),
    overdue: Number(row.overdue ?? 0),
    positive_count: Number(row.positive_count ?? 0),
    negative_count: Number(row.negative_count ?? 0),
    completion_rate: Number(row.completion_rate ?? 0),
    avg_completion_days:
      row.avg_completion_days != null ? Number(row.avg_completion_days) : null,
    overdue_rate_snapshot: Number(row.overdue_rate_snapshot ?? 0),
    avg_delay_risk_index:
      row.avg_delay_risk_index != null
        ? Number(row.avg_delay_risk_index)
        : null,
  };
}

export async function fetchWorkspaceTaskPerformanceTimeseries(
  workspaceId: string,
  from: string,
  to: string,
  granularity: "day" | "week" = "day",
  projectId?: string | null,
): Promise<TaskPerformanceTimeseriesPoint[]> {
  const { data, error } = await supabase.rpc(
    "get_workspace_task_performance_timeseries",
    {
      p_workspace_id: workspaceId,
      p_from: from,
      p_to: to,
      p_granularity: granularity,
      ...rpcProjectIdArg(projectId),
    },
  );

  if (error) throw error;

  return (data ?? []).map((row) => {
    const r = row as Record<string, unknown>;
    return {
      bucket_date: String(r.bucket_date),
      member_id: String(r.member_id),
      display_name: (r.display_name as string | null) ?? null,
      completed: Number(r.completed ?? 0),
      completed_late: Number(r.completed_late ?? 0),
      positive_count: Number(r.positive_count ?? 0),
      negative_count: Number(r.negative_count ?? 0),
    };
  });
}

/** Carrega tarefas + membros e calcula métricas (não depende da RPC desatualizada no Supabase). */
export async function fetchWorkspaceTaskMetrics(
  workspaceId: string,
  from: string,
  to: string,
): Promise<TaskMetricRow[]> {
  const { data: workspace, error: wsError } = await supabase
    .from("workspaces")
    .select("timezone")
    .eq("id", workspaceId)
    .maybeSingle();

  if (wsError) throw wsError;

  const timezone = workspace?.timezone ?? "America/Sao_Paulo";

  const { data: members, error: membersError } = await supabase
    .from("workspace_members")
    .select("id, display_name")
    .eq("workspace_id", workspaceId);

  if (membersError) throw membersError;

  const { data: tasks, error: tasksError } = await supabase
    .from("workspace_tasks")
    .select(
      "assigned_member_id, status, outcome, due_date, due_at, completed_at, updated_at",
    )
    .eq("workspace_id", workspaceId)
    .is("parent_task_id", null)
    .neq("status", "cancelled");

  if (tasksError) throw tasksError;

  return computeWorkspaceTaskMetrics(
    members ?? [],
    (tasks ?? []) as TaskForMetrics[],
    from,
    to,
    timezone,
  );
}
